export interface PaymentOptions {
    formId: string;
    eventId?: string;
    contactId?: string;
    applicationId?: string;
    successUrl: string;
    failureUrl: string;
    total: string | number;
    item: string;
    isCourseBooking?: boolean;
}

export interface StripeResponse {
    clientSecret: string;
    publicApiKey: string;
    amount: number;
}

declare global {
    const Stripe: any;
    interface Window {
        initializeEventBookingPayment: Function;
        initializeMembershipApplicationPayment: Function;
        initializeShopOrderPayment: Function;
    }
}

export class PaymentService {
    private stripe: any;
    private elements: any;
    private options: PaymentOptions | null = null;

    public async initializeEventBookingPayment(
        formId: string,
        eventId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string,
        isCourseBooking: boolean | string = false
    ): Promise<void> {
        await this.checkStatus();

        console.debug("initializeEventBookingPayment");

        this.options = {
            formId: formId,
            eventId: eventId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item,
            // Razor renders booleans as "True"/"False".
            isCourseBooking: isCourseBooking === true || String(isCourseBooking).toLowerCase() === "true"
        };

        const paymentForm = document.querySelector("#payment-form");
        paymentForm?.addEventListener("submit", this.handleSubmitEventBooking.bind(this));

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}&itemName=${this.options.item}&userJourney=Event Booking&bookingId=${this.options.eventId}&isCourseBooking=${this.options.isCourseBooking}`,
            {
                method: "POST"
            });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to initialize payment' }));
            const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
            console.error('Payment initialization failed:', errorMessage);
            this.showMessage(errorMessage);
            throw new Error(errorMessage);
        }

        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        // Initialize stripe
        this.stripe = Stripe(publicApiKey);

        const appearance = {
            theme: "stripe"
        };

        this.elements = this.stripe.elements({ appearance, clientSecret });

        const paymentElementOptions = {
            layout: "tabs"
        };
        const paymentElement = this.elements.create("payment", paymentElementOptions);
        paymentElement.mount("#payment-element");
    }

    public async initializeMembershipApplicationPayment(
        formId: string, 
        contactId: string, 
        applicationId: string, 
        successUrl: string, 
        failureUrl: string, 
        total: string | number, 
        item: string
    ): Promise<void> {
        await this.checkStatus();

        console.debug("initializeMembershipApplicationPayment");

        this.options = {
            formId: formId,
            contactId: contactId,
            applicationId: applicationId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item
        };

        const form = document.querySelector("#payment-form");
        form?.addEventListener("submit", this.handleSubmitMembershipApplication.bind(this));

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}&itemName=${this.options.item}&applicationId=${this.options.applicationId}&userJourney=Membership Application`,
            {
                method: "POST"
            });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to initialize payment' }));
            const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
            console.error('Payment initialization failed:', errorMessage);
            this.showMessage(errorMessage);
            throw new Error(errorMessage);
        }

        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        this.stripe = Stripe(publicApiKey);

        const appearance = {
            theme: "stripe"
        };

        this.elements = this.stripe.elements({ appearance, clientSecret });

        const paymentElementOptions = {
            layout: "tabs"
        };
        const paymentElement = this.elements.create("payment", paymentElementOptions);
        paymentElement.mount("#payment-element");
    }

    public async initializeShopOrderPayment(
        formId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number
    ): Promise<void> {
        await this.checkStatus();

        this.options = {
            formId: formId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: "Shop Order"
        };

        const paymentForm = document.querySelector("#payment-form");
        paymentForm?.addEventListener("submit", this.handleSubmitShopOrder.bind(this));

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}&itemName=${this.options.item}&userJourney=Shop Order`,
            {
                method: "POST"
            });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to initialize payment' }));
            const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
            console.error('Payment initialization failed:', errorMessage);
            this.showMessage(errorMessage);
            throw new Error(errorMessage);
        }

        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        // Initialize stripe
        this.stripe = Stripe(publicApiKey);

        const appearance = {
            theme: "stripe"
        };

        this.elements = this.stripe.elements({ appearance, clientSecret });

        const paymentElementOptions = {
            layout: "tabs"
        };
        const paymentElement = this.elements.create("payment", paymentElementOptions);
        paymentElement.mount("#payment-element");
    }

    private async handleSubmitEventBooking(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const bookingForm = document.getElementById(this.options.formId) as HTMLFormElement;

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/BookingSurface/SaveBookingToCrm?key=${this.options.eventId}&successUrl=${this.options.successUrl}&failureUrl=${this.options.failureUrl}`;

        // Validate form
        if (!bookingForm.checkValidity()) {
            bookingForm.reportValidity();
            return;
        } else {
            // save the form to session
            try {
                const form = document.getElementById(this.options.formId) as HTMLFormElement;
                const formData = JSON.stringify(this.getFormData(form));

                const response = await fetch(`/umbraco/surface/StripeSurface/SaveBookingFormToSession?key=${this.options.eventId}`, {
                    method: "POST",
                    body: formData,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    cache: 'no-cache'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

            } catch (err) {
                console.error(err);
                return;
            }
        }

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl
            }
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }

        this.setLoading(false);
    }

    private async handleSubmitMembershipApplication(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const appForm = document.getElementById(this.options.formId) as HTMLFormElement;

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/MembershipApplicationSurface/ConfirmStripePayment?key=${this.options.contactId}&successUrl=${encodeURIComponent(this.options.successUrl)}&failureUrl=${encodeURIComponent(this.options.failureUrl)}`;

        // Validate form
        if (!appForm.checkValidity()) {
            appForm.reportValidity();
            return;
        } else {
            // save the form to session
            try {
                const form = document.getElementById(this.options.formId) as HTMLFormElement;
                const formData = JSON.stringify(this.getFormData(form));

                const response = await fetch("/umbraco/surface/MembershipApplicationSurface/SavePaymentStep", {
                    method: "POST",
                    body: formData,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    cache: 'no-cache'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

            } catch (err) {
                console.error(err);
                return;
            }
        }

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl
            }
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }

        this.setLoading(false);
    }

    private async handleSubmitShopOrder(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const orderForm = document.getElementById(this.options.formId) as HTMLFormElement;
        if (!orderForm) {
            this.showMessage('Form error - please refresh the page');
            return;
        }

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/CheckoutSurface/SaveOrderToCrm?successUrl=${encodeURIComponent(this.options.successUrl)}&failureUrl=${encodeURIComponent(this.options.failureUrl)}`;

        // Validate form fields
        const formValid = orderForm.checkValidity();
        const addressValid = this.validateCheckoutAddress(orderForm);
        const emailValid = this.validateEmailMatch(orderForm);
        const billingValid = this.validateBillingAddress(orderForm);

        if (!formValid || !addressValid || !emailValid || !billingValid) {
            orderForm.reportValidity();
            return;
        }

        // Save form to session before payment
        try {
            const form = document.getElementById(this.options.formId) as HTMLFormElement;
            const formData = JSON.stringify(this.getFormData(form));

            const response = await fetch("/umbraco/surface/StripeSurface/SaveCheckoutFormToSession", {
                method: "POST",
                body: formData,
                headers: {
                    "Content-Type": "application/json"
                },
                cache: 'no-cache'
            });

            if (!response.ok) {
                this.showMessage('Failed to save order details. Please try again.');
                return;
            }

        } catch (err) {
            this.showMessage('Failed to save order details. Please try again.');
            return;
        }

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl
            }
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error) {
            if (error.type === "card_error" || error.type === "validation_error") {
                this.showMessage(error.message);
            } else {
                this.showMessage("An unexpected error occurred.");
            }
        }

        this.setLoading(false);
    }

    // Fetches the payment intent status after payment submission
    private async checkStatus(): Promise<void> {
        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);

        switch (paymentIntent.status) {
            case "succeeded":
                this.showMessage("Payment succeeded!");
                break;
            case "processing":
                this.showMessage("Your payment is processing.");
                break;
            case "requires_payment_method":
                this.showMessage("Your payment was not successful, please try again.");
                break;
            default:
                this.showMessage("Something went wrong.");
                break;
        }
    }

    // ------- UI helpers -------

    private showMessage(messageText: string): void {
        const messageContainer = document.querySelector("#payment-message");

        if (messageContainer) {
            messageContainer.classList.remove("d-none");
            messageContainer.textContent = messageText;

            setTimeout(() => {
                messageContainer.classList.add("d-none");
                messageContainer.textContent = "";
            }, 4000);
        }
    }

    // Show a spinner on payment submission
    private setLoading(isLoading: boolean): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement;
        const spinner = document.querySelector("#spinner");
        const buttonText = document.querySelector("#button-text") as HTMLElement;

        if (isLoading) {
            // Disable the button and show a spinner with "Processing..." text
            if (submitButton) submitButton.disabled = true;
            spinner?.classList.remove("d-none");
            if (buttonText) {
                buttonText.dataset.originalText = buttonText.textContent || "";
                buttonText.textContent = "Processing...";
            }
        } else {
            if (submitButton) submitButton.disabled = false;
            spinner?.classList.add("d-none");
            if (buttonText && buttonText.dataset.originalText) {
                buttonText.textContent = buttonText.dataset.originalText;
            }
        }
    }

    private getFormData(form: HTMLFormElement): { [key: string]: string } {
        const formData = new FormData(form);
        const indexedArray: { [key: string]: string } = {};

        formData.forEach((value, key) => {
            indexedArray[key] = value.toString();
        });

        return indexedArray;
    }

    /**
     * Validates checkout address fields (delivery address)
     * Shows manual address section and highlights missing fields if validation fails
     */
    private validateCheckoutAddress(form: HTMLFormElement): boolean {
        const requiredFields = [
            { name: 'AddressLine1', label: 'Address Line 1' },
            { name: 'City', label: 'City' },
            { name: 'Postcode', label: 'Postcode' }
        ];

        let isValid = true;
        const missingFields: string[] = [];

        for (const field of requiredFields) {
            const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement;

            if (!input || !input.value.trim()) {
                isValid = false;
                missingFields.push(field.label);

                const validationSpan = form.querySelector(`[data-valmsg-for="${field.name}"]`);
                if (validationSpan) {
                    validationSpan.textContent = `Please enter ${field.label.toLowerCase()}`;
                    validationSpan.classList.remove('field-validation-valid');
                    validationSpan.classList.add('field-validation-error');
                }

                if (input) {
                    input.classList.add('is-invalid');
                }
            }
        }

        if (!isValid) {
            const manualAddressSection = document.getElementById('delivery-manual-address');
            const lookupSection = document.querySelector('.addresslookup-container[data-name=""]');

            if (manualAddressSection) {
                manualAddressSection.style.display = 'block';
            }
            if (lookupSection) {
                (lookupSection as HTMLElement).style.display = 'none';
            }

            this.showMessage(`Please complete required address fields: ${missingFields.join(', ')}`);
        }

        return isValid;
    }

    /**
     * Validates billing address fields when SameAsDelivery is unchecked
     */
    private validateBillingAddress(form: HTMLFormElement): boolean {
        const sameAsDelivery = form.querySelector('[name="SameAsDelivery"]') as HTMLInputElement;

        // If "Same as above" is checked, skip billing validation
        if (sameAsDelivery?.checked) {
            return true;
        }

        const requiredFields = [
            { name: 'BillingSalutation', label: 'Title' },
            { name: 'BillingFirstName', label: 'First Name' },
            { name: 'BillingLastName', label: 'Last Name' },
            { name: 'BillingAddressLine1', label: 'Address Line 1' },
            { name: 'BillingCity', label: 'City' },
            { name: 'BillingPostcode', label: 'Postcode' },
            { name: 'BillingEmail', label: 'Email' },
            { name: 'BillingConfirmedEmail', label: 'Confirm Email' }
        ];

        let isValid = true;
        const missingFields: string[] = [];

        for (const field of requiredFields) {
            const input = form.querySelector(`[name="${field.name}"]`) as HTMLInputElement | HTMLSelectElement;

            if (!input || !input.value.trim()) {
                isValid = false;
                missingFields.push(field.label);

                // Show validation message if it exists
                const validationSpan = form.querySelector(`[data-valmsg-for="${field.name}"]`);
                if (validationSpan) {
                    validationSpan.textContent = `Please enter ${field.label.toLowerCase()}`;
                    validationSpan.classList.remove('field-validation-valid');
                    validationSpan.classList.add('field-validation-error');
                }

                if (input) {
                    input.classList.add('is-invalid');
                }
            }
        }

        // Also check billing emails match
        const billingEmail = form.querySelector('[name="BillingEmail"]') as HTMLInputElement;
        const billingConfirmedEmail = form.querySelector('[name="BillingConfirmedEmail"]') as HTMLInputElement;

        if (billingEmail?.value && billingConfirmedEmail?.value &&
            billingEmail.value.trim() !== billingConfirmedEmail.value.trim()) {
            isValid = false;
            missingFields.push('Emails do not match');

            const validationSpan = form.querySelector('[data-valmsg-for="BillingConfirmedEmail"]');
            if (validationSpan) {
                validationSpan.textContent = 'Billing email addresses do not match';
                validationSpan.classList.remove('field-validation-valid');
                validationSpan.classList.add('field-validation-error');
            }
            billingConfirmedEmail.classList.add('is-invalid');
        }

        if (!isValid) {
            // Show the billing manual address section
            const manualAddressSection = document.getElementById('billing-manual-address');
            const lookupSection = document.querySelector('.addresslookup-container[data-name="Billing"]');

            if (manualAddressSection) {
                manualAddressSection.style.display = 'block';
            }
            if (lookupSection) {
                (lookupSection as HTMLElement).style.display = 'none';
            }

            this.showMessage(`Please complete required billing fields: ${missingFields.join(', ')}`);
        }

        return isValid;
    }

    /**
     * Validates that email and confirmed email match
     */
    private validateEmailMatch(form: HTMLFormElement): boolean {
        const email = form.querySelector('[name="Email"]') as HTMLInputElement;
        const confirmedEmail = form.querySelector('[name="ConfirmedEmail"]') as HTMLInputElement;

        if (!email || !confirmedEmail) {
            return true; // Fields not found, skip validation
        }

        const emailValue = email.value.trim();
        const confirmedValue = confirmedEmail.value.trim();

        if (emailValue && confirmedValue && emailValue !== confirmedValue) {
            // Show validation message
            const validationSpan = form.querySelector('[data-valmsg-for="ConfirmedEmail"]');
            if (validationSpan) {
                validationSpan.textContent = 'Email addresses do not match';
                validationSpan.classList.remove('field-validation-valid');
                validationSpan.classList.add('field-validation-error');
            }

            confirmedEmail.classList.add('is-invalid');
            this.showMessage('Email addresses do not match');
            return false;
        }

        return true;
    }
}
